import { useState } from "react";
import { Check, FileText, Loader2, Plus, Upload, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAppNavigate } from "@/lib/use-app-navigate";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Revision option displayed for a document; `_id` is written to revisionId/v1RevisionId/v2RevisionId, with optional classification metadata for labels. */
export interface PickerRevision {
  _id: string;
  revisionNumber: number;
  revisionName: string;
  effectiveDocumentType?: string;
  classificationStatus?: string;
}

/** Selectable project document and its available revisions; each revision is sorted and rendered within this document's card. */
export interface PickerDocument {
  _id: string;
  name: string;
  revisions: PickerRevision[];
}

/** Selection for single-revision sections; `revisionId` is the chosen revision for `documentId` and may be blank until configured. */
export interface SingleRevisionSelection {
  documentId: string;
  revisionId: string;
}

/** Selection for pair-revision sections; `v1RevisionId` is the source revision and `v2RevisionId` is the newer target revision. */
export interface PairRevisionSelection {
  documentId: string;
  v1RevisionId: string;
  v2RevisionId: string;
}

/** Picker section configuration; selectionMode controls single vs multi cards, revisionMode controls one revision vs v1/v2 pairs, and optional allows empty sections. */
export interface PickerSection {
  key: string;
  label: string;
  documents: PickerDocument[];
  selectionMode: "single" | "multi";
  optional?: boolean;
  revisionMode: "single" | "pair";
  /** Optional override returning valid v2 choices for the selected document and v1 revision; defaults to newer revisions. */
  getAvailableV2Revisions?: (
    document: PickerDocument,
    v1RevisionId: string
  ) => PickerRevision[];
}

/** Single-document single-revision section value. */
export interface SingleSectionSelection {
  mode: "single";
  selection: SingleRevisionSelection | null;
}

/** Multi-document single-revision section value. */
export interface MultiSectionSelection {
  mode: "multi";
  selections: SingleRevisionSelection[];
}

/** Single-document pair-revision section value. */
export interface SinglePairSectionSelection {
  mode: "single-pair";
  selection: PairRevisionSelection | null;
}

/** Multi-document pair-revision section value. */
export interface MultiPairSectionSelection {
  mode: "multi-pair";
  selections: PairRevisionSelection[];
}

/** Controlled value for a section; the mode must match the section's selectionMode/revisionMode combination. */
export type SectionSelections =
  | SingleSectionSelection
  | MultiSectionSelection
  | SinglePairSectionSelection
  | MultiPairSectionSelection;

/** Props for the controlled picker; `isLoading` shows a spinner and `disabled` prevents document/revision changes. */
export interface ProjectDocumentPickerProps {
  sections: PickerSection[];
  values: Record<string, SectionSelections>;
  onChange: (sectionKey: string, value: SectionSelections) => void;
  isLoading: boolean;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sortedRevisions(revisions: PickerRevision[]) {
  return revisions.slice().sort(
    (a, b) =>
      a.revisionNumber - b.revisionNumber || a._id.localeCompare(b._id)
  );
}

function revLabel(r: PickerRevision) {
  return `${r.revisionName} (r${r.revisionNumber})`;
}

function defaultV2Revisions(
  doc: PickerDocument,
  v1Id: string
): PickerRevision[] {
  const v1 = doc.revisions.find((r) => r._id === v1Id);
  if (!v1) return [];
  return doc.revisions
    .filter((r) => r.revisionNumber > v1.revisionNumber)
    .sort(
      (a, b) =>
        a.revisionNumber - b.revisionNumber || a._id.localeCompare(b._id)
    );
}

function getSelectedDocIds(value: SectionSelections): Set<string> {
  switch (value.mode) {
    case "single":
      return value.selection ? new Set([value.selection.documentId]) : new Set();
    case "multi":
      return new Set(value.selections.map((s) => s.documentId));
    case "single-pair":
      return value.selection ? new Set([value.selection.documentId]) : new Set();
    case "multi-pair":
      return new Set(value.selections.map((s) => s.documentId));
  }
}

// ---------------------------------------------------------------------------
// Selected document card — single revision
// ---------------------------------------------------------------------------

function SelectedSingleCard({
  doc,
  revisionId,
  disabled,
  onRevisionChange,
  onRemove,
}: {
  doc: PickerDocument;
  revisionId: string;
  disabled: boolean;
  onRevisionChange: (revisionId: string) => void;
  onRemove: () => void;
}) {
  const revisions = sortedRevisions(doc.revisions);
  return (
    <div className="flex items-center gap-2 rounded-md border bg-background px-3 py-2">
      <FileText className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate text-sm font-medium">{doc.name}</span>
      <Select
        value={revisionId}
        onValueChange={onRevisionChange}
        disabled={disabled}
      >
        <SelectTrigger className="ml-auto h-7 w-auto min-w-[120px] gap-1 text-xs">
          <SelectValue placeholder="Revision" />
        </SelectTrigger>
        <SelectContent>
          {revisions.map((r) => (
            <SelectItem key={r._id} value={r._id}>
              {revLabel(r)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <button
        type="button"
        className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
        disabled={disabled}
        onClick={onRemove}
        aria-label={`Remove ${doc.name}`}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selected document card — pair revision (RevPilot)
// ---------------------------------------------------------------------------

function SelectedPairCard({
  doc,
  v1RevisionId,
  v2RevisionId,
  disabled,
  onV1Change,
  onV2Change,
  onRemove,
  getV2,
}: {
  doc: PickerDocument;
  v1RevisionId: string;
  v2RevisionId: string;
  disabled: boolean;
  onV1Change: (id: string) => void;
  onV2Change: (id: string) => void;
  onRemove: () => void;
  getV2: (doc: PickerDocument, v1Id: string) => PickerRevision[];
}) {
  const v1Revisions = sortedRevisions(doc.revisions);
  const v2Revisions = v1RevisionId ? getV2(doc, v1RevisionId) : [];

  return (
    <div className="space-y-2 rounded-md border bg-background px-3 py-2">
      <div className="flex items-center gap-2">
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate text-sm font-medium">
          {doc.name}
        </span>
        <button
          type="button"
          className="ml-auto shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-50"
          disabled={disabled}
          onClick={onRemove}
          aria-label={`Remove ${doc.name}`}
        >
          <X className="size-3.5" />
        </button>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select
          value={v1RevisionId}
          onValueChange={onV1Change}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="v1 (older)" />
          </SelectTrigger>
          <SelectContent>
            {v1Revisions.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {revLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={v2RevisionId}
          onValueChange={onV2Change}
          disabled={disabled || !v1RevisionId}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="v2 (newer)" />
          </SelectTrigger>
          <SelectContent>
            {v2Revisions.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {revLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-document combobox
// ---------------------------------------------------------------------------

function AddDocumentCombobox({
  documents,
  selectedIds,
  disabled,
  onSelect,
}: {
  documents: { sectionKey: string; sectionLabel: string; doc: PickerDocument }[];
  selectedIds: Set<string>;
  disabled: boolean;
  onSelect: (sectionKey: string, doc: PickerDocument) => void;
}) {
  const [open, setOpen] = useState(false);

  // Group by section
  const groups = new Map<string, { label: string; docs: PickerDocument[] }>();
  for (const item of documents) {
    const existing = groups.get(item.sectionKey);
    if (existing) {
      existing.docs.push(item.doc);
    } else {
      groups.set(item.sectionKey, {
        label: item.sectionLabel,
        docs: [item.doc],
      });
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="gap-1.5"
        >
          <Plus className="size-3.5" />
          Add document
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search documents..." />
          <CommandList>
            <CommandEmpty>No documents found.</CommandEmpty>
            {Array.from(groups.entries()).map(([key, group]) => (
              <CommandGroup key={key} heading={group.label}>
                {group.docs.map((doc) => {
                  const isSelected = selectedIds.has(doc._id);
                  return (
                    <CommandItem
                      key={doc._id}
                      value={`${doc.name} ${doc._id}`}
                      onSelect={() => {
                        onSelect(key, doc);
                        setOpen(false);
                      }}
                      disabled={isSelected}
                    >
                      <FileText className="size-3.5" />
                      <span className="truncate">{doc.name}</span>
                      {isSelected ? (
                        <Check className="ml-auto size-3.5 text-primary" />
                      ) : (
                        <Badge
                          variant="secondary"
                          className="ml-auto text-[10px] px-1 py-0"
                        >
                          {doc.revisions.length}
                        </Badge>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Section renderer
// ---------------------------------------------------------------------------

function PickerSectionView({
  section,
  value,
  onChange,
  disabled,
}: {
  section: PickerSection;
  value: SectionSelections;
  onChange: (value: SectionSelections) => void;
  disabled: boolean;
}) {
  const getV2 = section.getAvailableV2Revisions ?? defaultV2Revisions;

  // Single-select, single revision
  if (
    (value.mode === "single") &&
    section.revisionMode === "single"
  ) {
    const sel = value.selection;
    const doc = sel
      ? section.documents.find((d) => d._id === sel.documentId)
      : null;
    return doc && sel ? (
      <SelectedSingleCard
        doc={doc}
        revisionId={sel.revisionId}
        disabled={disabled}
        onRevisionChange={(revisionId) =>
          onChange({
            mode: "single",
            selection: { documentId: sel.documentId, revisionId },
          })
        }
        onRemove={() => onChange({ mode: "single", selection: null })}
      />
    ) : null;
  }

  // Multi-select, single revision
  if (value.mode === "multi" && section.revisionMode === "single") {
    return value.selections.length > 0 ? (
      <div className="space-y-1.5">
        {value.selections.map((sel) => {
          const doc = section.documents.find((d) => d._id === sel.documentId);
          if (!doc) return null;
          return (
            <SelectedSingleCard
              key={sel.documentId}
              doc={doc}
              revisionId={sel.revisionId}
              disabled={disabled}
              onRevisionChange={(revisionId) =>
                onChange({
                  mode: "multi",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { ...s, revisionId }
                      : s
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  mode: "multi",
                  selections: value.selections.filter(
                    (s) => s.documentId !== sel.documentId
                  ),
                })
              }
            />
          );
        })}
      </div>
    ) : null;
  }

  // Single-select, pair revision
  if (value.mode === "single-pair" && section.revisionMode === "pair") {
    const sel = value.selection;
    const doc = sel
      ? section.documents.find((d) => d._id === sel.documentId)
      : null;
    return doc && sel ? (
      <SelectedPairCard
        doc={doc}
        v1RevisionId={sel.v1RevisionId}
        v2RevisionId={sel.v2RevisionId}
        disabled={disabled}
        getV2={getV2}
        onV1Change={(v1RevisionId) =>
          onChange({
            mode: "single-pair",
            selection: { ...sel, v1RevisionId, v2RevisionId: "" },
          })
        }
        onV2Change={(v2RevisionId) =>
          onChange({
            mode: "single-pair",
            selection: { ...sel, v2RevisionId },
          })
        }
        onRemove={() => onChange({ mode: "single-pair", selection: null })}
      />
    ) : null;
  }

  // Multi-select, pair revision
  if (value.mode === "multi-pair") {
    return value.selections.length > 0 ? (
      <div className="space-y-1.5">
        {value.selections.map((sel) => {
          const doc = section.documents.find((d) => d._id === sel.documentId);
          if (!doc) return null;
          return (
            <SelectedPairCard
              key={sel.documentId}
              doc={doc}
              v1RevisionId={sel.v1RevisionId}
              v2RevisionId={sel.v2RevisionId}
              disabled={disabled}
              getV2={getV2}
              onV1Change={(v1RevisionId) =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { ...s, v1RevisionId, v2RevisionId: "" }
                      : s
                  ),
                })
              }
              onV2Change={(v2RevisionId) =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { ...s, v2RevisionId }
                      : s
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.filter(
                    (s) => s.documentId !== sel.documentId
                  ),
                })
              }
            />
          );
        })}
      </div>
    ) : null;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Renders the controlled multi-section document picker and emits section-scoped selection updates through onChange. */
export function ProjectDocumentPicker({
  sections,
  values,
  onChange,
  isLoading,
  disabled = false,
}: ProjectDocumentPickerProps) {
  const navigate = useAppNavigate();

  // Collect all selected doc IDs across all sections
  const allSelectedIds = new Set<string>();
  for (const section of sections) {
    const val = values[section.key];
    if (val) {
      for (const id of getSelectedDocIds(val)) allSelectedIds.add(id);
    }
  }

  // Build flattened list for the combobox dropdown
  const comboboxDocs: {
    sectionKey: string;
    sectionLabel: string;
    doc: PickerDocument;
  }[] = [];
  for (const section of sections) {
    for (const doc of section.documents) {
      comboboxDocs.push({
        sectionKey: section.key,
        sectionLabel: section.label,
        doc,
      });
    }
  }

  const handleComboboxSelect = (sectionKey: string, doc: PickerDocument) => {
    const section = sections.find((s) => s.key === sectionKey);
    if (!section) return;

    const autoRev =
      doc.revisions.length === 1 ? doc.revisions[0]!._id : "";
    const val = values[sectionKey];

    if (section.revisionMode === "pair") {
      if (section.selectionMode === "single") {
        onChange(sectionKey, {
          mode: "single-pair",
          selection: { documentId: doc._id, v1RevisionId: "", v2RevisionId: "" },
        });
        return;
      }

      const prev =
        val?.mode === "multi-pair" ? val.selections : [];
      onChange(sectionKey, {
        mode: "multi-pair",
        selections: [
          ...prev.filter((s) => s.documentId !== doc._id),
          { documentId: doc._id, v1RevisionId: "", v2RevisionId: "" },
        ],
      });
      return;
    }

    if (section.selectionMode === "single") {
      onChange(sectionKey, {
        mode: "single",
        selection: { documentId: doc._id, revisionId: autoRev },
      });
    } else {
      const prev = val?.mode === "multi" ? val.selections : [];
      if (prev.some((s) => s.documentId === doc._id)) return;
      onChange(sectionKey, {
        mode: "multi",
        selections: [
          ...prev,
          { documentId: doc._id, revisionId: autoRev },
        ],
      });
    }
  };

  const hasAnySelected = allSelectedIds.size > 0;

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold leading-none tracking-tight">
            Source Documents
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Add documents from your project library and select revisions.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-lg border py-8 text-sm text-muted-foreground">
          <Loader2 className="mr-2 size-4 animate-spin" />
          Loading project documents...
        </div>
      ) : (
        <>
          {/* Selected document cards grouped by section */}
          {sections.map((section) => {
            const val = values[section.key];
            if (!val) return null;
            const hasItems =
              (val.mode === "single" && val.selection !== null) ||
              (val.mode === "multi" && val.selections.length > 0) ||
              (val.mode === "single-pair" && val.selection !== null) ||
              (val.mode === "multi-pair" && val.selections.length > 0);
            if (!hasItems) return null;
            return (
              <div key={section.key}>
                {sections.length > 1 ? (
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </p>
                ) : null}
                <PickerSectionView
                  section={section}
                  value={val}
                  onChange={(v) => onChange(section.key, v)}
                  disabled={disabled}
                />
              </div>
            );
          })}

          {!hasAnySelected ? (
            <div className="rounded-md border border-dashed bg-muted/30 px-4 py-5 text-center text-sm text-muted-foreground">
              No documents selected. Click &ldquo;Add document&rdquo; to get started.
            </div>
          ) : null}

          <AddDocumentCombobox
            documents={comboboxDocs}
            selectedIds={allSelectedIds}
            disabled={disabled}
            onSelect={handleComboboxSelect}
          />
        </>
      )}

      <div className="border-t pt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() =>
            navigate({ to: "/project/documents", title: "Project Documents" })
          }
        >
          <Upload className="size-4" />
          Upload to Project Documents
        </Button>
        <p className="mt-1 text-xs text-muted-foreground">
          Upload documents to your project library, then select them here.
        </p>
      </div>
    </section>
  );
}
